import { holdingUnitLifecycleStatusValues } from '@crv/contracts/holding-units';
import type {
  HoldingUnitCapabilities,
  HoldingUnitLifecycleStatus,
  TareProfileValidationResult,
  UnitProfileValidationIssue,
  UnitProfileValidationResult,
  UnitProfileWorkflow,
} from '@crv/contracts';

import { deriveUnitProfileEffectiveCapabilities } from '../domain/resolution.js';
import {
  getUnitProfileTemplateDefinition,
  type UnitProfileTemplateDefinition as CoreUnitProfileTemplateDefinition,
} from '../domain/templates.js';
import {
  deriveUnitProfileCapabilityPolicy,
  validateCapabilityPolicyWithinTemplate,
  validateTareProfile,
  validateUnitProfileTareCompatibility,
} from '../domain/validation.js';
import { isUnitProfileWorkflowEligible } from '../domain/workflow-eligibility.js';

export {
  buildStarterUnitProfileDefaults,
  type StarterProfileDefaults,
  type StarterProfileDefaultsOptions,
  type StarterTareProfileDefinition,
  type StarterUnitProfileDefinition,
} from '../domain/starter-defaults.js';
export {
  UNIT_PROFILE_CAPABILITY_NAMES,
  getUnitProfileTemplateDefinition,
  listUnitProfileTemplateDefinitions,
  type UnitProfileCapabilityName,
  type UnitProfileTemplateDefinition,
} from '../domain/templates.js';
export {
  deriveUnitProfileCapabilityPolicy,
  validateCapabilityPolicyWithinTemplate,
  validateTareProfile,
  validateUnitProfileStructuralEdit,
  validateUnitProfileTareCompatibility,
} from '../domain/validation.js';

export interface UnitProfileAdminCapabilityPolicyInput {
  templateKey: string;
  capabilityPolicy?: Partial<HoldingUnitCapabilities> | null;
}

export interface UnitProfileAdminTarePreviewInput {
  mode: string;
  defaultTareLb?: number | null;
}

export interface UnitProfileAdminPreviewInput extends UnitProfileAdminCapabilityPolicyInput {
  tareProfile?: UnitProfileAdminTarePreviewInput | null;
  isActive?: boolean;
}

export interface UnitProfileAdminPreviewValidation {
  isValid: boolean;
  capabilityPolicy: UnitProfileValidationResult;
  tareProfile: TareProfileValidationResult;
  tareCompatibility: UnitProfileValidationResult;
}

export interface UnitProfileLifecycleCapabilityPreview {
  isValid: boolean;
  issues: UnitProfileValidationIssue[];
  capabilityPolicy: HoldingUnitCapabilities | null;
  effectiveCapabilitiesByLifecycleStatus: Record<
    HoldingUnitLifecycleStatus,
    HoldingUnitCapabilities
  > | null;
}

export interface UnitProfileWorkflowEligibilityPreview {
  workflow: UnitProfileWorkflow;
  isActive: boolean;
  isEligible: boolean;
  validation: UnitProfileAdminPreviewValidation;
}

export function validateUnitProfileAdminCapabilityPolicy(
  input: UnitProfileAdminCapabilityPolicyInput,
): UnitProfileValidationResult {
  return validateCapabilityPolicyWithinTemplate(input.templateKey, input.capabilityPolicy ?? {});
}

export function validateUnitProfileAdminTareCompatibility(input: {
  templateKey: string;
  tareProfile?: UnitProfileAdminTarePreviewInput | null;
}): UnitProfileValidationResult {
  const template = getUnitProfileTemplateDefinition(input.templateKey);
  if (!template) {
    return invalidTemplateValidationResult();
  }

  return validateUnitProfileTareCompatibility(
    {
      templateKey: template.templateKey,
      fixedIdPolicy: template.fixedIdPolicy,
    },
    input.tareProfile,
  );
}

export function validateUnitProfileAdminPreviewInput(
  input: UnitProfileAdminPreviewInput,
): UnitProfileAdminPreviewValidation {
  const capabilityPolicy = validateUnitProfileAdminCapabilityPolicy(input);
  const tareProfile = input.tareProfile
    ? validateTareProfile(input.tareProfile)
    : validTareProfileValidationResult();
  const tareCompatibility = validateUnitProfileAdminTareCompatibility(input);

  return {
    isValid: capabilityPolicy.isValid && tareProfile.isValid && tareCompatibility.isValid,
    capabilityPolicy,
    tareProfile,
    tareCompatibility,
  };
}

export function previewUnitProfileLifecycleCapabilities(
  input: UnitProfileAdminCapabilityPolicyInput,
): UnitProfileLifecycleCapabilityPreview {
  const template = getUnitProfileTemplateDefinition(input.templateKey);
  if (!template) {
    return {
      isValid: false,
      issues: invalidTemplateValidationResult().issues,
      capabilityPolicy: null,
      effectiveCapabilitiesByLifecycleStatus: null,
    };
  }

  const capabilityValidation = validateUnitProfileAdminCapabilityPolicy(input);
  if (!capabilityValidation.isValid) {
    return {
      isValid: false,
      issues: capabilityValidation.issues,
      capabilityPolicy: null,
      effectiveCapabilitiesByLifecycleStatus: null,
    };
  }

  const capabilityPolicy = deriveUnitProfileCapabilityPolicy(
    input.templateKey,
    input.capabilityPolicy,
  );

  return {
    isValid: true,
    issues: [],
    capabilityPolicy,
    effectiveCapabilitiesByLifecycleStatus: buildLifecycleCapabilityPreview(
      template,
      capabilityPolicy,
    ),
  };
}

export function previewUnitProfileWorkflowEligibility(input: {
  templateKey: string;
  workflow: UnitProfileWorkflow;
  capabilityPolicy?: Partial<HoldingUnitCapabilities> | null;
  tareProfile?: UnitProfileAdminTarePreviewInput | null;
  isActive?: boolean;
  includeInactive?: boolean;
}): UnitProfileWorkflowEligibilityPreview {
  const isActive = input.isActive ?? true;
  const validation = validateUnitProfileAdminPreviewInput(input);
  const template = getUnitProfileTemplateDefinition(input.templateKey);

  if (!template || !validation.isValid || (!input.includeInactive && !isActive)) {
    return {
      workflow: input.workflow,
      isActive,
      isEligible: false,
      validation,
    };
  }

  const capabilityPolicy = deriveUnitProfileCapabilityPolicy(
    input.templateKey,
    input.capabilityPolicy,
  );

  return {
    workflow: input.workflow,
    isActive,
    isEligible: isUnitProfileWorkflowEligible(
      {
        profileCategory: template.profileCategory,
        fixedIdPolicy: template.fixedIdPolicy,
        capabilityPolicy,
      },
      input.workflow,
    ),
    validation,
  };
}

function buildLifecycleCapabilityPreview(
  template: CoreUnitProfileTemplateDefinition,
  capabilityPolicy: HoldingUnitCapabilities,
): Record<HoldingUnitLifecycleStatus, HoldingUnitCapabilities> {
  const result = {} as Record<HoldingUnitLifecycleStatus, HoldingUnitCapabilities>;
  for (const lifecycleStatus of holdingUnitLifecycleStatusValues) {
    result[lifecycleStatus] = deriveUnitProfileEffectiveCapabilities({
      profile: template,
      capabilityPolicy,
      lifecycleStatus,
    });
  }
  return result;
}

function validTareProfileValidationResult(): TareProfileValidationResult {
  return {
    isValid: true,
    issues: [],
  };
}

function invalidTemplateValidationResult(): UnitProfileValidationResult {
  return {
    isValid: false,
    issues: [
      {
        code: 'invalid_template_key',
        field: 'templateKey',
        message: 'Unit profile templateKey is not recognized.',
      },
    ],
  };
}
